// OidcAuthProvider (GIY-037): oidc-client-ts com PKCE (authorization code) contra o provedor GIYUS (OpenIddict, ADR-002/004),
// renovação silenciosa (`prompt=none` em iframe → /auth/silent-callback), step-up (`prompt=login&acr_values=…&max_age=0`),
// logout RP-initiated com revogação do refresh token e tokens só em memória (Parte 3.5 / 14.2).
import {
  InMemoryWebStorage,
  type User,
  UserManager,
  type UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';

import {
  type HintStorage,
  browserHintStorage,
  hasSessionHint,
  setSessionHint,
} from './sessionHint';
import { AUTH_CALLBACK_PATH, AUTH_SILENT_CALLBACK_PATH, type AuthBackend } from './types';

export interface OidcAuthOptions {
  readonly authority: string;
  readonly clientId: string;
  /** Origem da SPA (ex.: `https://app-dev.giyus.com.br`). */
  readonly origin: string;
  /** Quando `true` (produção), renderiza o `EntraAccessPortal` exclusivamente com login Microsoft Entra ID em vez de redirecionar para `/account/login`. */
  readonly useEntraPortal?: boolean;
  /** Fábrica injetável (testes). */
  readonly createUserManager?: (settings: UserManagerSettings) => UserManager;
  /** Armazenamento do indício de sessão (padrão `sessionStorage`; `null` desliga). */
  readonly hintStorage?: HintStorage | null;
}

export const ENTRA_BACKEND_TOKEN_STORAGE_KEY = 'giyus.oidc.entra_backend_token';
export const ENTRA_BACKEND_EXPIRES_STORAGE_KEY = 'giyus.oidc.entra_backend_expires_at';
export const ENTRA_RETURN_TO_STORAGE_KEY = 'giyus.entra.return_to';

export const STEP_UP_ACR = 'urn:giyus:acr:mfa';

/**
 * Escopos pedidos pela SPA (Parte 3.5.1; exatamente os registrados pelo provedor — `OidcScopeSeed`): `giyus.api` é a
 * audiência da API e `offline_access` liga o refresh token rotativo. Um escopo desconhecido (ex.: `email`) faz o
 * `authorize` responder `invalid_scope` antes de qualquer tela.
 */
export const OIDC_SCOPE = 'openid profile giyus.api offline_access';

interface RedirectState {
  readonly returnTo?: string;
}

export function readReturnTo(state: unknown): string {
  if (state && typeof state === 'object' && 'returnTo' in state) {
    const value = (state as RedirectState).returnTo;
    if (typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')) {
      return value;
    }
  }
  return '/';
}

export function buildUserManagerSettings(options: OidcAuthOptions): UserManagerSettings {
  return {
    authority: options.authority,
    client_id: options.clientId,
    redirect_uri: `${options.origin}${AUTH_CALLBACK_PATH}`,
    silent_redirect_uri: `${options.origin}${AUTH_SILENT_CALLBACK_PATH}`,
    post_logout_redirect_uri: `${options.origin}/`,
    response_type: 'code',
    scope: OIDC_SCOPE,
    automaticSilentRenew: true,
    includeIdTokenInSilentRenew: false,
    silentRequestTimeoutInSeconds: 10,
    monitorSession: false,
    loadUserInfo: false,
    // REQ-AUTH-015: sair revoga o refresh token no provedor (`signOut` chama `revokeTokens` explicitamente, tolerando
    // falha — o logout RP-initiated encerra a sessão de qualquer forma); com `revokeTokensOnSignout` uma revogação com
    // erro abortaria o logout inteiro.
    revokeTokensOnSignout: false,
    revokeTokenTypes: ['refresh_token'],
    // Tokens só em memória: nada em localStorage/sessionStorage (Parte 3.5).
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    // O estado do redirect (state, code_verifier do PKCE, returnTo) NÃO é token e precisa sobreviver à ida e volta ao
    // provedor (a página descarrega): sessionStorage, o padrão do oidc-client-ts — em memória o callback nunca casaria.
    stateStore: new WebStorageStateStore({ store: redirectStateStorage() }),
  };
}

function redirectStateStorage(): Storage {
  return typeof sessionStorage === 'undefined' ? new InMemoryWebStorage() : sessionStorage;
}

export class OidcAuthBackend implements AuthBackend {
  readonly mode = 'oidc' as const;
  private readonly manager: UserManager;
  private readonly hintStorage: HintStorage | null;
  private readonly useEntraPortal: boolean;
  private readonly sessionLostListeners = new Set<() => void>();
  private externalAccessToken: string | null = null;
  private externalExpiresAtMs = 0;
  private externalExpiryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: OidcAuthOptions) {
    const settings = buildUserManagerSettings(options);
    this.manager = options.createUserManager
      ? options.createUserManager(settings)
      : new UserManager(settings);
    this.hintStorage =
      options.hintStorage === undefined ? browserHintStorage() : options.hintStorage;
    this.useEntraPortal = options.useEntraPortal ?? false;
    this.restoreExternalSession();
  }

  private scheduleExternalExpiryTimer(): void {
    if (this.externalExpiryTimer !== null) {
      clearTimeout(this.externalExpiryTimer);
      this.externalExpiryTimer = null;
    }
    if (!this.externalAccessToken || this.externalExpiresAtMs <= 0) {
      return;
    }
    const remainingMs = Math.max(0, this.externalExpiresAtMs - Date.now());
    this.externalExpiryTimer = setTimeout(() => {
      this.externalExpiryTimer = null;
      this.notifySessionLost();
    }, remainingMs);
  }

  private restoreExternalSession(): void {
    try {
      if (typeof sessionStorage === 'undefined') {
        return;
      }
      const token = sessionStorage.getItem(ENTRA_BACKEND_TOKEN_STORAGE_KEY);
      const rawExp = sessionStorage.getItem(ENTRA_BACKEND_EXPIRES_STORAGE_KEY);
      const exp = rawExp ? Number(rawExp) : 0;
      if (token && exp > Date.now()) {
        this.externalAccessToken = token;
        this.externalExpiresAtMs = exp;
        this.scheduleExternalExpiryTimer();
      } else if (token || rawExp) {
        this.clearExternalSession();
      }
    } catch {
      // Ignore storage access errors
    }
  }

  setExternalSession(accessToken: string, expiresInSeconds = 3600): void {
    const expiresAtMs = Date.now() + expiresInSeconds * 1000;
    this.externalAccessToken = accessToken;
    this.externalExpiresAtMs = expiresAtMs;
    setSessionHint(this.hintStorage, true);
    this.scheduleExternalExpiryTimer();
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(ENTRA_BACKEND_TOKEN_STORAGE_KEY, accessToken);
        sessionStorage.setItem(ENTRA_BACKEND_EXPIRES_STORAGE_KEY, String(expiresAtMs));
      }
    } catch {
      // Ignore storage access errors
    }
  }

  private clearExternalSession(): void {
    if (this.externalExpiryTimer !== null) {
      clearTimeout(this.externalExpiryTimer);
      this.externalExpiryTimer = null;
    }
    this.externalAccessToken = null;
    this.externalExpiresAtMs = 0;
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(ENTRA_BACKEND_TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(ENTRA_BACKEND_EXPIRES_STORAGE_KEY);
      }
    } catch {
      // Ignore storage access errors
    }
  }

  readonly notifySessionLost = (): void => {
    this.clearExternalSession();
    setSessionHint(this.hintStorage, false);
    for (const listener of this.sessionLostListeners) {
      listener();
    }
  };

  async initialize(): Promise<boolean> {
    this.restoreExternalSession();
    if (this.externalAccessToken && this.externalExpiresAtMs > Date.now()) {
      return true;
    }
    const current = await this.manager.getUser();
    if (current && !current.expired) {
      return true;
    }
    if (!hasSessionHint(this.hintStorage) || this.useEntraPortal) {
      // Primeira visita nesta aba: sem indício de sessão não há o que renovar — direto ao login interativo.
      return false;
    }
    try {
      const renewed = await this.manager.signinSilent();
      const ok = renewed !== null && !renewed.expired;
      setSessionHint(this.hintStorage, ok);
      return ok;
    } catch {
      setSessionHint(this.hintStorage, false);
      return false;
    }
  }

  signIn(returnTo: string): Promise<void> {
    if (this.useEntraPortal) {
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(ENTRA_RETURN_TO_STORAGE_KEY, returnTo);
        }
      } catch {
        // Ignore storage errors
      }
      return Promise.resolve();
    }
    return this.manager.signinRedirect({ state: { returnTo } satisfies RedirectState });
  }

  async completeSignIn(): Promise<string> {
    const user: User | undefined = await this.manager.signinCallback();
    setSessionHint(this.hintStorage, true);
    return readReturnTo(user?.state);
  }

  async signOut(): Promise<void> {
    const hadExternalSession = Boolean(this.externalAccessToken);
    this.clearExternalSession();
    setSessionHint(this.hintStorage, false);
    if (hadExternalSession || this.useEntraPortal) {
      return;
    }
    try {
      // Melhor esforço (REQ-AUTH-015): o /connect/logout encerra a sessão e a família mesmo se isto falhar.
      await this.manager.revokeTokens(['refresh_token']);
    } catch {
      // nunca bloqueia o logout
    }
    await this.manager.signoutRedirect();
  }

  async getAccessToken(): Promise<string | null> {
    if (this.externalAccessToken) {
      if (this.externalExpiresAtMs > Date.now()) {
        return this.externalAccessToken;
      }
      this.notifySessionLost();
      return null;
    }
    const user = await this.manager.getUser();
    if (!user || user.expired) {
      return null;
    }
    return user.access_token;
  }

  /** Parte 3.5.3: senha + segundo fator de novo no provedor; a ação pendente é retomada após o callback. */
  async stepUp(returnTo: string): Promise<boolean> {
    await this.manager.signinRedirect({
      prompt: 'login',
      acr_values: STEP_UP_ACR,
      max_age: 0,
      state: { returnTo } satisfies RedirectState,
    });
    return true;
  }

  readonly onSessionLost = (handler: () => void): (() => void) => {
    this.sessionLostListeners.add(handler);
    const onLost = (): void => {
      this.clearExternalSession();
      setSessionHint(this.hintStorage, false);
      handler();
    };
    const events = this.manager.events as typeof this.manager.events | undefined;
    events?.addSilentRenewError(onLost);
    events?.addAccessTokenExpired(onLost);
    return () => {
      this.sessionLostListeners.delete(handler);
      events?.removeSilentRenewError(onLost);
      events?.removeAccessTokenExpired(onLost);
    };
  };
}
